import { Injectable } from '@angular/core';

import { BehaviorSubject, fromEvent as observableFromEvent, Observable, Subject } from 'rxjs';
import { map, skip, takeUntil } from 'rxjs/operators';

import { Colors as DarkTheme } from './../theme/dark/ts/colors';
import { Colors as LightTheme } from './../theme/light/ts/colors';

export const SupportedThemes = { light: LightTheme, dark: DarkTheme };

export type SupportedThemesT = keyof typeof SupportedThemes;
export type Theme = (typeof SupportedThemes)[SupportedThemesT];
export type ThemeName = SupportedThemesT | 'system';

export interface ThemeI {
  themeName: ThemeName;
  colorTheme: SupportedThemesT;
}

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  public static readonly DEFAULT_THEME: SupportedThemesT = 'light';
  private static readonly LS_KEY: string = 'recruitee-theme';

  public activeTheme$: Observable<Theme>;
  public activeThemeName$: Observable<ThemeName>;
  public systemThemeChange: Observable<Event>;

  public stopListeningToSystemThemeChange$ = new Subject<void>();

  private supportedThemes$: BehaviorSubject<ThemeI> = new BehaviorSubject({
    themeName: this.retrieveCachedTheme(),
    colorTheme: this.retrieveCachedSupportedTheme(),
  });

  constructor() {
    this.systemThemeChange = observableFromEvent(
      window.matchMedia('(prefers-color-scheme: dark)'),
      'change',
    );

    if (this.retrieveCachedTheme() === 'system') {
      this.listenToSystemThemeChange();
    }

    this.activeTheme$ = this.supportedThemes$
      .asObservable()
      .pipe(
        map(
          (theme: ThemeI) =>
            SupportedThemes[theme.colorTheme] || SupportedThemes[ThemeService.DEFAULT_THEME],
        ),
      );

    this.activeThemeName$ = this.supportedThemes$
      .asObservable()
      .pipe(map(theme => theme.themeName));

    this.supportedThemes$.pipe(skip(1)).subscribe((theme: ThemeI) => {
      this.cacheTheme(theme.themeName);
      this.setThemeHTMLData(theme.colorTheme);
    });
  }

  public resolveCurrentSystemTheme(): void {
    this.switchTheme({
      themeName: 'system',
      colorTheme: this.getSystemColorTheme(),
    });
  }

  public listenToSystemThemeChange(): void {
    this.systemThemeChange
      .pipe(takeUntil(this.stopListeningToSystemThemeChange$))
      .subscribe((event: MediaQueryListEvent) => {
        const name = event.matches ? 'dark' : 'light';
        this.switchTheme({
          themeName: 'system',
          colorTheme: name,
        });
      });
  }

  public switchTheme(supportedTheme: ThemeI): void {
    this.supportedThemes$.next(supportedTheme);
  }

  private getSystemColorTheme(): SupportedThemesT {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private setThemeHTMLData(theme: SupportedThemesT): void {
    document.documentElement.setAttribute('data-theme', theme);
  }

  private retrieveCachedTheme(): ThemeName {
    return <ThemeName>localStorage.getItem(ThemeService.LS_KEY) || ThemeService.DEFAULT_THEME;
  }

  private retrieveCachedSupportedTheme(): SupportedThemesT {
    if (localStorage.getItem(ThemeService.LS_KEY) === 'system') {
      return this.getSystemColorTheme();
    } else {
      return (
        <SupportedThemesT>localStorage.getItem(ThemeService.LS_KEY) || ThemeService.DEFAULT_THEME
      );
    }
  }

  private cacheTheme(theme: ThemeName): void {
    localStorage.setItem(ThemeService.LS_KEY, theme);
  }
}
